import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Loader, Paper, Text } from '@mantine/core'
import { useOrpc } from '@/hooks/useClient'
import type { AccountingReport } from '@/model/middleware-types'
import { formatCalendarDate } from '../utils'
import { DetailsTable } from './DetailsTable'
import { ExportCsvButton } from './ExportCsvButton'
import { ReportControls } from './ReportControls'
import { ReportFilters } from './ReportFilters'
import {
  type AccountingFilters,
  type AccountingState,
  type AccountingView,
  buildReportInput,
  getAccountingRangeError,
  getDefaultAccountingFilters,
  getDefaultAccountingView,
  GROUP_BY_LABELS,
  persistAccountingState,
  readPersistedAccountingState,
  SUMMARY_TITLES,
} from './state'
import { SummaryCards } from './SummaryCards'
import { type ReportGroup, SummaryTable } from './SummaryTable'

type DetailsSelection =
  | { kind: 'all' }
  | { kind: 'group'; group: ReportGroup }
  | { kind: 'card'; label: string }

export function AccountingReportsPage() {
  const orpc = useOrpc()
  const [, setSearchParams] = useSearchParams()

  const [state, setState] = useState<AccountingState>(readPersistedAccountingState)
  const [selection, setSelection] = useState<DetailsSelection>({ kind: 'all' })
  const { filters, view } = state

  useEffect(() => {
    persistAccountingState(state)
  }, [state])

  const rangeError = getAccountingRangeError(filters)
  const input = buildReportInput(filters, view)

  const reportQuery = useQuery(
    orpc.billingV2Report.queryOptions({
      input,
      enabled: !rangeError,
      placeholderData: keepPreviousData,
    }),
  )
  const filterOptionsQuery = useQuery(
    orpc.billingV2ReportFilterOptions.queryOptions({
      input: { dateFrom: filters.dateFrom, dateTo: filters.dateTo },
      enabled: !rangeError,
    }),
  )
  const data = reportQuery.data

  const changeFilters = (next: AccountingFilters) => {
    setState({ filters: next, view })
    setSelection({ kind: 'all' })
  }

  const changeView = (next: AccountingView) => {
    setState({ filters, view: next })
    setSelection({ kind: 'all' })
  }

  const resetFilters = () => {
    setState({ filters: getDefaultAccountingFilters(), view: getDefaultAccountingView() })
    setSelection({ kind: 'all' })
  }

  const selectCard = (report: AccountingReport, label: string) => {
    setState({ filters, view: { ...view, report } })
    setSelection({ kind: 'card', label })
  }

  const openEncounter = (encounterId: string) => {
    setSearchParams(
      (params) => {
        params.set('drawer', encounterId)
        return params
      },
      { replace: true },
    )
  }

  const detailsRows =
    data && selection.kind === 'group'
      ? data.details.filter((row) =>
          input.groupBy === 'method'
            ? 'method' in row && row.method.key === selection.group.key
            : row[input.groupBy].key === selection.group.key,
        )
      : (data?.details ?? [])

  const detailsTitle =
    selection.kind === 'group'
      ? `${selection.group.label} · supporting details`
      : selection.kind === 'card'
        ? `${selection.label} · supporting details`
        : 'Supporting details'

  const summaryNote =
    data?.report === 'payments'
      ? `Patient receipts include collected co-payments, deductibles, co-insurance, and self-pay payments. ${data.detailsTotal} cash transactions · Select a row to drill down. CSV exports the details currently shown.`
      : `${data?.detailsTotal ?? 0} encounters · Select a row to drill down. CSV exports the details currently shown.`

  const showReport = !rangeError && data !== undefined

  return (
    <div className="flex flex-col gap-4 p-4">
      <ReportFilters
        filters={filters}
        onChange={changeFilters}
        options={filterOptionsQuery.data}
        onReset={resetFilters}
      />

      {rangeError ? (
        <Text role="alert" c="red">
          {rangeError}
        </Text>
      ) : (
        <Text size="sm" c="dimmed">
          {`All reports use visits in the selected service-date range. Payments and adjustments through ${formatCalendarDate(filters.dateTo)}. Charges after adjustments − payments for selected visits = outstanding balance.`}
        </Text>
      )}

      {showReport && (
        <SummaryCards
          cards={data.cards}
          asOf={data.asOf}
          activeReport={view.report}
          onSelect={selectCard}
        />
      )}

      <Paper withBorder radius="md" className="flex flex-col gap-3 p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <ReportControls view={view} onChange={changeView} />
          <ExportCsvButton
            input={input}
            groupKey={selection.kind === 'group' ? selection.group.key : undefined}
            disabled={Boolean(rangeError) || data === undefined}
          />
        </div>

        {!rangeError && reportQuery.isError && <Text c="red">Failed to load the report</Text>}
        {!rangeError && !reportQuery.isError && data === undefined && <Loader />}
        {showReport && !reportQuery.isError && (
          <SummaryTable
            title={SUMMARY_TITLES[data.report]}
            groupByLabel={GROUP_BY_LABELS[input.groupBy]}
            columns={data.columns}
            groups={data.groups}
            totalCents={data.totalCents}
            note={summaryNote}
            onDrillDown={(group) => {
              if (reportQuery.isPlaceholderData) return

              setSelection({ kind: 'group', group })
            }}
          />
        )}
      </Paper>

      {showReport && !reportQuery.isError && (
        <DetailsTable
          report={data.report}
          title={detailsTitle}
          rows={detailsRows}
          truncated={data.truncated}
          showAllVisible={selection.kind !== 'all'}
          onShowAll={() => setSelection({ kind: 'all' })}
          onOpenEncounter={openEncounter}
        />
      )}
    </div>
  )
}
